import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { getRulesetIcon } from "@/components/icon/getRulesetIcon.js";
import Image from "next/image";
import { PopupButton } from "@/components/generic/popup/PopupButton.js";
import React from "react";
import Link from "next/link";
import type {
    APIGame,
    APIGamePlayer, APIPublicGamePreferences,
    APIUser } from "@rosette/ts/src/business/api/api_schema.js";
import {
    defaultGamePreferences,
    getGamePlayerDisplayName,
} from "@rosette/ts/src/business/api/api_schema.js";
import { FadingDiv } from "@/components/layout/FadingDiv.js";
import { useFormatDate } from "@rosette/web_utils/src/time/useFormatDate.js";
import { Path } from "@rosette/ts/src/util/Path.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { getNameColour } from "@rosette/web_components/src/user/getNameColour.js";
import { GameEndReason } from "@rosette/ts/src/business/game/GameEndReason.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { calculateAccuracy } from "@rosette/ts/src/business/analysis/calculateAccuracy.js";
import { formatRatingChange } from "@rosette/ts/src/business/analysis/formatRatingChange.js";

/** The game list, whose loose paragraphs (status, "view more") read larger than the entries. */
export const GAME_LIST = "min-h-[40em] [:where(&)_p]:text-xl";
export const GAME_ENTRY_CONTAINER = "mb-3 rounded-xl";

const GAME_ENTRY = cn(
    "flex max-w-lg flex-row items-stretch overflow-hidden rounded-xl border-b-[0.3rem]",
    "text-base text-body no-underline hover:no-underline",
);
export const VIEW_MORE_ENTRY = cn(
    GAME_ENTRY,
    "cursor-pointer border-b-surface-sunken bg-surface-reading",
    "hover:border-b-surface-reading hover:bg-surface-neutral",
);
const RESULT_BAR = "flex min-w-6 flex-col items-start justify-center";


interface PlayerNameProps {
    player: APIGamePlayer | undefined;
    boldName: boolean;
}


function PlayerName({ player, boldName }: PlayerNameProps) {
    let preferences: APIPublicGamePreferences = defaultGamePreferences;
    if (player && player.userPreferences) {
        preferences = player.userPreferences;
    }
    const nameColour = getNameColour(preferences.nameColour);

    return (
        <span
            className={cn(
                boldName ? "font-bold" : null,
            )}
            style={{
                color: nameColour.colorCSS,
            }}>

            {getGamePlayerDisplayName(player)}
        </span>
    );
}


interface GameListEntryProps {
    user: APIUser;
    game: APIGame;
}


export function GameListEntry({ user, game }: GameListEntryProps) {
    const gameMode = GameMode.getByID(game.gameMode);
    const ruleset = Ruleset.getByID(game.rulesetID);
    const endReason = GameEndReason.getByID(game.endReasonID);
    const light = game.lightPlayer;
    const dark = game.darkPlayer;

    const winner = (game.didLightWin ? light : dark);
    const loser = (game.didLightWin ? dark : light);

    const isLight = (light && light?.user?.publicID === user.publicID);
    const didWin = !!(isLight ? game.didLightWin : !game.didLightWin);
    const hasWinner = (
        endReason !== GameEndReason.LEFT_BEFORE_BEGIN
        && endReason !== GameEndReason.SERVER
    );

    const dateStr = useFormatDate(game.createdAt);
    const gameHref = new Path(`/g/${game.publicID}`);

    const winnerName = (
        <PlayerName player={winner} boldName={didWin} />
    );
    const loserName = (
        <PlayerName player={loser} boldName={!didWin} />
    );

    let accuracyStr: string | null = null;
    let ratingChangeStr: string | null = null;
    let ratingIncreased: boolean = false;
    let isRatingCleared = false;
    if (game.analysis) {
        isRatingCleared = game.analysis.ratingCleared && (didWin || game.analysis.ratingAddedBackToLoser);
        const moveCount = (
            isLight
                ? game.analysis.lightMoveCount
                : game.analysis.darkMoveCount
        );
        const initialRating = (
            isLight
                ? game.analysis.lightInitialRating
                : game.analysis.darkInitialRating
        );
        const ratingChange = (
            isLight
                ? game.analysis.lightRatingChange
                : game.analysis.darkRatingChange
        );

        if (moveCount > 5) {
            const meanMoveDelta = (
                isLight
                    ? game.analysis.lightMeanMoveDelta
                    : game.analysis.darkMeanMoveDelta
            );
            const accuracy = calculateAccuracy(ruleset, meanMoveDelta);
            accuracyStr = `${Math.floor(accuracy)}% accuracy`;
        }

        if (initialRating !== null && ratingChange !== null) {
            ratingIncreased = (ratingChange > 0);
            ratingChangeStr = `${formatRatingChange(initialRating, ratingChange)} rating`;
        }
    }

    let desc;
    if (endReason === GameEndReason.WIN) {
        desc = (
            <>
                {winnerName}
                {" defeated "}
                {loserName}
            </>
        );

    } else if (endReason === GameEndReason.RESIGNATION) {
        desc = (
            <>
                {winnerName}
                {" won, as "}
                {loserName}
                {" resigned"}
            </>
        );

    } else if (endReason === GameEndReason.LEFT) {
        desc = (
            <>
                {winnerName}
                {" won, as "}
                {loserName}
                {" stopped playing"}
            </>
        );

    } else if (endReason === GameEndReason.LEFT_BEFORE_BEGIN) {
        desc = (
            <>
                {loserName}
                {" never started playing against "}
                {winnerName}
            </>
        );

    } else {
        throw new Error(`Unhandled end reason ${endReason.getName()}`);
    }

    return (
        <div className={GAME_ENTRY_CONTAINER}>
            <Link
                className={cn(
                    GAME_ENTRY,
                    "border-b-surface-reading bg-surface-neutral shadow-md",
                    "hover:border-b-subtle hover:bg-surface-hover",
                )}
                href={gameHref.toString()}>

                <div className={cn(
                    RESULT_BAR,
                    hasWinner ? (didWin ? "bg-success" : "bg-danger") : "bg-surface-hover",
                )} />
                <div className="flex grow flex-col items-start justify-center py-3 pr-2 pl-4">
                    <p className="m-0 text-base">
                        {desc}
                    </p>
                    <p className="m-0 text-base text-subtle">
                        {dateStr}
                    </p>
                    <p className="m-0 mt-2 text-base text-subtle">
                        {`${gameMode.getName()} game`}

                        {accuracyStr !== null && ", "}
                        {accuracyStr !== null && (
                            <span>
                                {accuracyStr}
                            </span>
                        )}

                        {ratingChangeStr !== null && ", "}
                        {ratingChangeStr !== null && (
                            <span className={cn(
                                (ratingIncreased ? "text-primary" : "text-danger"),
                                isRatingCleared ? "line-through opacity-60" : null,
                            )}>
                                {ratingChangeStr}
                            </span>
                        )}
                    </p>
                </div>

                <PopupButton
                    buttonClassName="flex flex-col items-start justify-center pt-2 pr-3 pb-1 pl-2"
                    buttonContent={(
                        <Image
                            className="w-[3em] max-w-[3em] min-w-[3em] object-contain"
                            src={getRulesetIcon(ruleset)}
                            alt={ruleset.getDesc()}
                            title={ruleset.getName()} />
                    )}>

                    <p className="mb-0">
                        <span className="font-bold">
                            {ruleset.getName()}
                            {" "}
                            Rules.
                        </span>
                        {" "}
                        <span>
                            {ruleset.getIntroDesc()}
                        </span>
                    </p>
                </PopupButton>
            </Link>
        </div>
    );
}


interface GameEntriesProps {
    user: APIUser;
    games: APIGame[] | null | undefined;
    gamesLoading: boolean;
    gamesError: Error | null;
}


export function GameEntries({
    user, games, gamesLoading, gamesError,
}: GameEntriesProps) {

    if (!games || games.length === 0 || gamesLoading || gamesError) {
        let status = "This empty space is waiting to be filled with your future victories!";
        if (gamesLoading) {
            status = "Loading recent games...";
        } else if (gamesError) {
            console.error(gamesError);
            status = `Error loading recent games: ${gamesError.message}`;
        }
        return (
            <FadingDiv options={{ delay: gamesLoading ? 0.25 : 0.0 }}>
                <p>{status}</p>
            </FadingDiv>
        );
    }

    const gameElems = [];
    for (const game of games) {
        gameElems.push(
            <GameListEntry
                key={game.publicID}
                user={user}
                game={game} />,
        );
    }
    return (
        <FadingDiv
            key="games"
            className="overflow-hidden">

            {gameElems}
        </FadingDiv>
    );
}
